import api from "./api";

// Administração da API pública (integração Nuklie). Backend: /api/v1/public-api

export type PublicApiInfo = {
    version: string;
    base_url: string;
    openapi_url: string;
    docs_url: string;
    token_prefix: string;
    user_header: string;
    webhook_headers: { timestamp: string; signature: string };
};

export type PublicApiScope = { scope: string; description: string };

export type PublicApiToken = {
    id: string;
    name: string;
    token_prefix: string;
    scopes: string[];
    rate_limit_per_minute: number;
    status: 'active' | 'revoked';
    created_by: { id: string; name: string | null } | null;
    last_used_at: string | null;
    revoked_at: string | null;
    created_at: string;
    // Só na resposta da criação
    token?: string;
};

export type PublicApiWebhook = {
    id: string;
    url: string;
    events: string[];
    description: string | null;
    status: 'active' | 'disabled';
    failed_count: number;
    pending_count: number;
    created_at: string;
    // Só na resposta da criação
    secret?: string;
};

export type PublicApiDelivery = {
    id: string;
    event_id: string;
    event: string;
    status: 'pending' | 'delivered' | 'failed';
    attempts: number;
    next_attempt_at: string | null;
    last_status_code: number | null;
    last_error: string | null;
    delivered_at: string | null;
    created_at: string;
};

export type CategorySettings = { duration_minutes?: number; is_meeting?: boolean; member_ids?: string[] };

export type SchedulingSettings = {
    timezone: string;
    work_days: number[];
    work_start: string;
    work_end: string;
    breaks: { start: string; end: string }[];
    slot_interval_minutes: number;
    default_duration_minutes: number;
    min_notice_minutes: number;
    busy_statuses: string[];
    categories: Record<string, CategorySettings>;
};

const data = (response: any) => response.data?.data;

/** Mensagem do envelope { error: { message } } da API pública. */
export const publicApiErrorMessage = (error: any, fallback: string) =>
    error?.response?.data?.error?.message || error?.response?.data?.message || fallback;

export const getPublicApiInfo = async (): Promise<PublicApiInfo> => data(await api.get('public-api/info'));
export const getPublicApiScopes = async (): Promise<PublicApiScope[]> => data(await api.get('public-api/scopes'));
export type PublicApiEvent = {
    event: string;
    label: string;
    resource: 'contact' | 'opportunity' | 'task' | 'ticket' | 'charge' | 'subscription' | 'webhook';
    description: string;
    trigger: string;   // quando é enviado
    data: string;      // o que vem em data
    use_case: string;  // exemplo de automação
};

export const getPublicApiEvents = async (): Promise<PublicApiEvent[]> => data(await api.get('public-api/events'));

export const getPublicApiTokens = async (): Promise<PublicApiToken[]> => data(await api.get('public-api/tokens'));
export const postPublicApiToken = async (body: { name: string; scopes: string[]; rate_limit_per_minute: number }): Promise<PublicApiToken> =>
    data(await api.post('public-api/tokens', body));
export const deletePublicApiToken = async (id: string) => data(await api.delete(`public-api/tokens/${id}`));

export const getPublicApiWebhooks = async (): Promise<PublicApiWebhook[]> => data(await api.get('public-api/webhooks'));
export const postPublicApiWebhook = async (body: { url: string; events: string[]; description?: string }): Promise<PublicApiWebhook> =>
    data(await api.post('public-api/webhooks', body));
export const deletePublicApiWebhook = async (id: string) => data(await api.delete(`public-api/webhooks/${id}`));
export const postPublicApiWebhookTest = async (id: string) => data(await api.post(`public-api/webhooks/${id}/test`));
export const getPublicApiDeliveries = async (id: string): Promise<PublicApiDelivery[]> => data(await api.get(`public-api/webhooks/${id}/deliveries`));

export const getPublicApiCategories = async (): Promise<{ id: string; name: string }[]> => data(await api.get('public-api/categories'));
export const getPublicApiSettings = async (): Promise<{ scheduling: SchedulingSettings }> => data(await api.get('public-api/settings'));
export const putPublicApiSettings = async (scheduling: Partial<SchedulingSettings>): Promise<{ scheduling: SchedulingSettings }> =>
    data(await api.put('public-api/settings', { scheduling }));
